import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  Grid,
  Tooltip,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import StepActions from "./StepActions";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import { createReportItem } from "../../services/activityReportService";
import { useLocation } from "react-router-dom";

const schema = yup.object({
  report_name: yup.string().required("Request name is required"),
  startDate: yup.string().when("period", {
    is: "Custom",
    then: () => yup.string().required("Start date is required"),
    otherwise: () => yup.string().notRequired(),
  }),
  endDate: yup.string().when("period", {
    is: "Custom",
    then: () => yup.string().required("End date is required"),
    otherwise: () => yup.string().notRequired(),
  }),
  period: yup.string().required("Period is required"),
  month: yup.string().when("period", {
    is: "Monthly",
    then: () => yup.string().required("Month is required"),
    otherwise: () => yup.string().notRequired(),
  }),
  quarter: yup.string().when("period", {
    is: "Quarterly",
    then: () => yup.string().required("Quarter is required"),
    otherwise: () => yup.string().notRequired(),
  }),
  annual: yup.string().when(["period"], {
    is: (period) => period === "Monthly" || period === "Quarterly" || period === "Annual",
    then: () => yup.string().required("Year is required"),
    otherwise: () => yup.string().notRequired(),
  }),
  dueDate: yup.string().required("Due date is required"),
});

const Step1Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const requestId = location.state?.requestId;
  const existingData = location.state?.settingsData;

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      report_name: "",
      period: "",
      month: "",
      quarter: "",
      annual: "",
      startDate: "",
      endDate: "",
      dueDate: "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    if (existingData) {
      // Derive month and year from period_start_date as fallback
      // when month/annual were not explicitly saved on the object
      const startDate = existingData?.period_start_date
        ? new Date(existingData.period_start_date)
        : null;

      const derivedMonth = existingData?.month
        ? parseInt(existingData.month, 10)
        : startDate
          ? startDate.getMonth() + 1   // getMonth() is 0-indexed
          : "";

      const derivedYear = existingData?.annual
        ? parseInt(existingData.annual, 10)
        : startDate
          ? startDate.getFullYear()
          : "";

      // Derive quarter from period_start_date month
      const derivedQuarter = existingData?.quarter
        ? existingData.quarter
        : startDate
          ? (() => {
            const m = startDate.getMonth() + 1; // 1-12
            if (m <= 3) return "q1";
            if (m <= 6) return "q2";
            if (m <= 9) return "q3";
            return "q4";
          })()
          : "";

      reset({
        report_name: existingData?.report_name || "",
        period:
          existingData?.request_type === 1 ? "Monthly" :
            existingData?.request_type === 2 ? "Quarterly" :
              existingData?.request_type === 3 ? "Annual" :
                existingData?.request_type === 4 ? "Custom" : "",
        month: derivedMonth,          // Always a number (e.g. 4), matches monthOptions values
        quarter: derivedQuarter,
        annual: derivedYear,          // Always a number (e.g. 2026), matches yearOptions values
        startDate: existingData?.startDate
          ? existingData.startDate
          : existingData?.period_start_date
            ? new Date(existingData.period_start_date).toISOString().split("T")[0]
            : "",
        endDate: existingData?.endDate
          ? existingData.endDate
          : existingData?.period_end_date
            ? new Date(existingData.period_end_date).toISOString().split("T")[0]
            : "",
        dueDate: existingData?.due_date
          ? new Date(existingData.due_date).toISOString().split("T")[0]
          : existingData?.dueDate || "",
      });
    }
  }, [existingData, reset]);

  const isEditMode = location.state?.isEditMode || false;

  const watchPeriod = watch("period");

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return [
      /*  { value: currentYear - 4, label: `${currentYear - 4}` },
       { value: currentYear - 3, label: `${currentYear - 3}` },
       { value: currentYear - 2, label: `${currentYear - 2}` },
       { value: currentYear - 1, label: `${currentYear - 1}` }, */
      { value: currentYear, label: `${currentYear} (Current)` },
      { value: currentYear + 1, label: `${currentYear + 1} (Next Year)` },
    ];
  };

  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const quarterOptions = [
    { value: "q1", label: "Q1 (Jan - Mar)" },
    { value: "q2", label: "Q2 (Apr - Jun)" },
    { value: "q3", label: "Q3 (Jul - Sep)" },
    { value: "q4", label: "Q4 (Oct - Dec)" },
  ];

  const yearOptions = getYearOptions();

  const labelStyle = {
    fontWeight: 500,
    mb: 1,
    color: "#6B7280",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: 0.5,
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "24px",
      backgroundColor: "#fff",
      "& fieldset": { borderColor: "#E5E7EB" },
    },
    marginBottom: 2,
  };

  const defineReqestType = (period) => {
    if (period === "Monthly") return 1;
    else if (period === "Quarterly") return 2;
    else if (period === "Annual") return 3;
    else if (period === "Custom") return 4;
  };

  const onSubmit = (data) => {
    // Format the data to be sent to next step
    console.log("Raw form data on submit:", data);

    let reqType = defineReqestType(data.period);

    let period_start_date = "";
    let period_end_date = "";

    if (data.period === "Monthly") {
      // For months 1-12, set period_start_date to the first day of the selected month
      const year = data.annual || new Date().getFullYear().toString();
      console.log("Selected month value:", data.month);
      const monthNum = String(data.month).padStart(2, "0");
      period_start_date = dayjs(`${year}-${monthNum}-01`)
        .startOf("day")
        .toISOString();
      // Set period_end_date to the last day of the selected month
      period_end_date = dayjs(`${year}-${monthNum}-01`)
        .endOf("month")
        .endOf("day")
        .toISOString();
    } else if (data.period === "Quarterly") {
      const year = data.annual;
      // For quarters, set period_start_date to the first day of the first month in the quarter
      const quarterStartMonths = { q1: "01", q2: "04", q3: "07", q4: "10" };
      const monthNum = quarterStartMonths[data.quarter];
      period_start_date = dayjs(`${year}-${monthNum}-01`)
        .startOf("day")
        .toISOString();
      // Set period_end_date to the last day of the last month in the quarter
      const quarterEndMonths = { q1: "03", q2: "06", q3: "09", q4: "12" };
      const endMonthNum = quarterEndMonths[data.quarter];
      period_end_date = dayjs(`${year}-${endMonthNum}-01`)
        .endOf("month")
        .endOf("day")
        .toISOString();
    } else if (data.period === "Annual") {
      const year = data.annual;
      // For annual, set period_start_date to January 1st of the selected year
      period_start_date = dayjs(`${year}-01-01`)
        .startOf("day")
        .toISOString();
      // Set period_end_date to December 31st of the selected year
      period_end_date = dayjs(`${year}-12-31`)
        .endOf("day")
        .toISOString();
    } else if (data.period === "Custom") {
      // For custom, use the selected start date
      period_start_date = dayjs(data.startDate).startOf("day").toISOString();
      period_end_date = dayjs(data.endDate).endOf("day").toISOString();
    }

    const settingsData = {
      report_name: data.report_name,
      request_type: reqType,
      period_end_date: period_end_date,
      period_start_date: period_start_date,
      due_date: data.dueDate,
    };

    // Add period-specific data
    if (data.period === "Monthly") {
      settingsData.month = data.month;
      // Find the selected month to get both value and label
      const selectedMonth = monthOptions.find(
        (m) => m.value === parseInt(data.month),
      );
      settingsData.monthLabel = selectedMonth
        ? selectedMonth.label
        : data.month;
      settingsData.annual = data.annual;
    } else if (data.period === "Quarterly") {
      settingsData.quarter = data.quarter;
      const selectedQuarter = quarterOptions.find(
        (q) => q.value === data.quarter,
      );
      settingsData.quarterLabel = selectedQuarter
        ? selectedQuarter.label
        : data.quarter;
      settingsData.annual = data.annual;

    } else if (data.period === "Annual") {
      settingsData.annual = data.annual;
    } else if (data.period === "Custom") {
      settingsData.startDate = data.startDate;
      settingsData.endDate = data.endDate;
    }

    console.log("Settings Data:", settingsData);

    // Pass data to next step via navigate state
    navigate(
      requestId
        ? `/create-request/${requestId}/companies`
        : `/create-request/companies`,
      {
        state: {
          settingsData: settingsData,
          companiesData: location.state?.companiesData || [],
          reportData: location.state?.reportData || [],
          requestId: location.state?.requestId || null,
          isEditMode: isEditMode,
        },
      }
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ pb: 12 }}>
        <Box sx={{ maxWidth: 800, mx: "auto", mt: 6 }}>
          <Box
            component="form"
            sx={{
              bgcolor: "#FAFAFA",
              p: 2,
              borderRadius: "16px",
              border: "1px solid #F3F4F6",
              boxShadow: "0px 1px 3px rgba(0,0,0,0.05)",
            }}
          >
            {/* Settings Header */}
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <SettingsOutlinedIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
              <Typography variant="subtitle1" fontWeight={700}>
                Settings
              </Typography>
            </Box>

            {/* Request Name */}
            <Box>
              <Typography sx={labelStyle}>Add a report request name</Typography>
              <TextField
                fullWidth
                placeholder="Enter a name"
                size="small"
                sx={inputSx}
                {...register("report_name")}
                error={!!errors.report_name}
                helperText={errors.report_name?.message}
              />
            </Box>

            {/* Period Selection */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={labelStyle}>
                Period
                <Tooltip
                  title="Select the period for which the report will be requested"
                  arrow
                  placement="top"
                >
                  <InfoOutlinedIcon
                    sx={{ fontSize: 16, color: "#9CA3AF", cursor: "pointer" }}
                  />
                </Tooltip>
              </Typography>
              <Controller
                name="period"
                control={control}
                render={({ field }) => (
                  <ToggleButtonGroup
                    value={field.value}
                    exclusive
                    onChange={(e, newValue) => {
                      field.onChange(newValue);
                      // Reset dependent fields when period changes
                      setValue("month", "");
                      setValue("quarter", "");
                      setValue("annual", "");
                      setValue("startDate", "");
                      setValue("endDate", "");
                    }}
                    sx={{
                      gap: 1,
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        borderRadius: "20px !important",
                        border: "1px solid #E5E7EB !important",
                        px: 3,
                        py: 0.5,
                        fontWeight: 600,
                        color: "#374151",
                        "&.Mui-selected": {
                          backgroundColor: "#E0F2FE",
                          color: "#0369A1",
                          borderColor: "#0369A1 !important",
                        },
                      },
                    }}
                  >
                    <ToggleButton value="Monthly">Monthly</ToggleButton>
                    <ToggleButton value="Quarterly">Quarterly</ToggleButton>
                    <ToggleButton value="Annual">Annual</ToggleButton>
                    <ToggleButton value="Custom">Custom</ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
              {errors.period && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "block" }}
                >
                  {errors.period.message}
                </Typography>
              )}
            </Box>

            {/* Dynamic Row */}
            <Grid container spacing={3}>
              <Grid item sx={{ width: "48%" }}>
                {watchPeriod === "Monthly" && (
                  <>
                    <Typography sx={labelStyle}>Select month</Typography>
                    <Controller
                      name="month"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          fullWidth
                          size="small"
                          displayEmpty
                          error={!!errors.month}
                          renderValue={(selected) => {
                            if (selected === "" || selected === null || selected === undefined) {
                              return (
                                <span style={{ color: "#9CA3AF" }}>
                                  Select month
                                </span>
                              );
                            }
                            const selectedMonth = monthOptions.find(
                              (m) => m.value === parseInt(selected, 10),
                            );
                            return selectedMonth ? selectedMonth.label : selected;
                          }}
                          sx={{
                            borderRadius: "24px",
                            backgroundColor: "#fff",
                            mb: 2,
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select month
                          </MenuItem>
                          {monthOptions.map((month) => (
                            <MenuItem key={month.value} value={month.value}>
                              {month.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.month && (
                      <Typography variant="caption" color="error">
                        {errors.month.message}
                      </Typography>
                    )}
                    {/* Add Year Selector for Monthly */}
                    {/* Add Year Selector for Monthly */}
                    <Typography sx={labelStyle}>Select year</Typography>
                    <Controller
                      name="annual"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          fullWidth
                          size="small"
                          displayEmpty
                          error={!!errors.annual}
                          renderValue={(selected) => {
                            if (selected === "" || selected === null || selected === undefined) {
                              return (
                                <span style={{ color: "#9CA3AF" }}>
                                  Select year
                                </span>
                              );
                            }
                            const selectedYear = yearOptions.find(
                              (y) => y.value === parseInt(selected, 10),
                            );
                            return selectedYear ? selectedYear.label : selected;
                          }}
                          sx={{
                            borderRadius: "24px",
                            backgroundColor: "#fff",
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select year
                          </MenuItem>
                          {yearOptions.map((year) => (
                            <MenuItem key={year.value} value={year.value}>
                              {year.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.annual && (
                      <Typography variant="caption" color="error">
                        {errors.annual.message}
                      </Typography>
                    )}
                  </>
                )}
                {watchPeriod === "Quarterly" && (
                  <>
                    <Typography sx={labelStyle}>Select quarter</Typography>
                    <Controller
                      name="quarter"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          fullWidth
                          size="small"
                          displayEmpty
                          error={!!errors.quarter}
                          renderValue={(selected) => {
                            if (!selected || selected.length === 0) {
                              return (
                                <span style={{ color: "#9CA3AF" }}>
                                  Select quarter
                                </span>
                              );
                            }
                            const selectedQuarter = quarterOptions.find(
                              (q) => q.value === selected,
                            );
                            return selectedQuarter
                              ? selectedQuarter.label
                              : selected;
                          }}
                          sx={{
                            borderRadius: "24px",
                            backgroundColor: "#fff",
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select quarter
                          </MenuItem>
                          {quarterOptions.map((quarter) => (
                            <MenuItem key={quarter.value} value={quarter.value}>
                              {quarter.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />

                    {/* Add Year Selector for Quarterly */}
                    <Typography sx={labelStyle}>Select year</Typography>
                    <Controller
                      name="annual"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          fullWidth
                          size="small"
                          displayEmpty
                          error={!!errors.annual}
                          renderValue={(selected) => {
                            if (selected === "" || selected === null || selected === undefined) {
                              return (
                                <span style={{ color: "#9CA3AF" }}>
                                  Select year
                                </span>
                              );
                            }
                            const selectedYear = yearOptions.find(
                              (y) => y.value === parseInt(selected, 10),
                            );
                            return selectedYear ? selectedYear.label : selected;
                          }}
                          sx={{
                            borderRadius: "24px",
                            backgroundColor: "#fff",
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select year
                          </MenuItem>
                          {yearOptions.map((year) => (
                            <MenuItem key={year.value} value={year.value}>
                              {year.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />

                    {errors.quarter && (
                      <Typography variant="caption" color="error">
                        {errors.quarter.message}
                      </Typography>
                    )}
                  </>
                )}
                {watchPeriod === "Annual" && (
                  <>
                    <Typography sx={labelStyle}>Select year</Typography>
                    <Controller
                      name="annual"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          fullWidth
                          size="small"
                          displayEmpty
                          error={!!errors.annual}
                          renderValue={(selected) => {
                            if (selected === "" || selected === null || selected === undefined) {
                              return (
                                <span style={{ color: "#9CA3AF" }}>
                                  Select year
                                </span>
                              );
                            }
                            const selectedYear = yearOptions.find(
                              (y) => y.value === parseInt(selected, 10),
                            );
                            return selectedYear ? selectedYear.label : selected;
                          }}
                          sx={{
                            borderRadius: "24px",
                            backgroundColor: "#fff",
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select year
                          </MenuItem>
                          {yearOptions.map((year) => (
                            <MenuItem key={year.value} value={year.value}>
                              {year.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.annual && (
                      <Typography variant="caption" color="error">
                        {errors.annual.message}
                      </Typography>
                    )}
                  </>
                )}

                {watchPeriod === "Custom" && (
                  <>
                    <Typography sx={labelStyle}>
                      Start date
                      <Tooltip
                        title="This is the start date the report will cover"
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: 16, color: "#9CA3AF" }}
                        />
                      </Tooltip>
                    </Typography>
                    <Controller
                      name="startDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) =>
                            field.onChange(
                              date ? date.format("YYYY-MM-DD") : "",
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: inputSx,
                              placeholder: "Select start date",
                              error: !!errors.startDate,
                              helperText: errors.startDate?.message,
                            },
                          }}
                        />
                      )}
                    />

                    <Typography sx={labelStyle}>
                      End date
                      <Tooltip
                        title="This is the end date the report will cover"
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: 16, color: "#9CA3AF" }}
                        />
                      </Tooltip>
                    </Typography>
                    <Controller
                      name="endDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) =>
                            field.onChange(
                              date ? date.format("YYYY-MM-DD") : "",
                            )
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: inputSx,
                              placeholder: "Select end date",
                              error: !!errors.endDate,
                              helperText: errors.endDate?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </>
                )}
              </Grid>

              {/* Always visible Due Date or End Date for Custom */}
              <Grid item sx={{ width: "48%" }}>
                {watchPeriod === "Custom" ? (
                  <>
                    <Typography sx={labelStyle}>
                      Due date
                      <Tooltip
                        title="The report will be due on this date"
                        arrow
                        placement="top"
                      >
                        <InfoOutlinedIcon
                          sx={{ fontSize: 16, color: "#9CA3AF" }}
                        />
                      </Tooltip>
                    </Typography>
                    <Controller
                      name="dueDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) =>
                            field.onChange(
                              date ? date.format("YYYY-MM-DD") : "",
                            )
                          }
                          minDate={dayjs()}       // ← ADD THIS — disables all dates before today
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: inputSx,
                              placeholder: "Select due date",
                              error: !!errors.dueDate,
                              helperText: errors.dueDate?.message,
                            },
                          }}
                        />
                      )}
                    />
                  </>
                ) : (
                  watchPeriod && (
                    <>
                      <Typography sx={labelStyle}>
                        Due date
                        <Tooltip
                          title="The report will be due on this date"
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            sx={{ fontSize: 16, color: "#9CA3AF" }}
                          />
                        </Tooltip>
                      </Typography>
                      <Controller
                        name="dueDate"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            value={field.value ? dayjs(field.value) : null}
                            onChange={(date) =>
                              field.onChange(
                                date ? date.format("YYYY-MM-DD") : "",
                              )
                            }
                            minDate={dayjs()}       // ← ADD THIS — disables all dates before today
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                                sx: inputSx,
                                placeholder: "Select due date",
                                error: !!errors.dueDate,
                                helperText: errors.dueDate?.message,
                              },
                            }}
                          />
                        )}
                      />
                    </>
                  )
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* StepActions Component */}
        <StepActions
          onNext={handleSubmit(onSubmit)}
          onPreview={() => console.log("Preview")}
          onSave={async () => {
            console.log("Draft data:", watch());

            const data = watch();

            let period_start_date = "";
            let period_end_date = "";

            if (data.period === "Monthly") {
              const monthNum = String(data.month).padStart(2, "0");

              period_start_date = dayjs(`${data.annual}-${monthNum}-01`)
                .startOf("day")
                .toISOString();

              period_end_date = dayjs(`${data.annual}-${monthNum}-01`)
                .endOf("month")
                .endOf("day")
                .toISOString();

            } else if (data.period === "Quarterly") {

              const quarterStartMonths = {
                q1: "01",
                q2: "04",
                q3: "07",
                q4: "10",
              };

              const monthNum = quarterStartMonths[data.quarter];

              period_start_date = dayjs(`${data.annual}-${monthNum}-01`)
                .startOf("day")
                .toISOString();

              const quarterEndMonths = {
                q1: "03",
                q2: "06",
                q3: "09",
                q4: "12",
              };

              const endMonthNum = quarterEndMonths[data.quarter];

              period_end_date = dayjs(`${data.annual}-${endMonthNum}-01`)
                .endOf("month")
                .endOf("day")
                .toISOString();

            } else if (data.period === "Annual") {

              period_start_date = dayjs(`${data.annual}-01-01`)
                .startOf("day")
                .toISOString();

              period_end_date = dayjs(`${data.annual}-12-31`)
                .endOf("day")
                .toISOString();

            } else if (data.period === "Custom") {

              period_start_date = dayjs(data.startDate)
                .startOf("day")
                .toISOString();

              period_end_date = dayjs(data.endDate)
                .endOf("day")
                .toISOString();
            }

            try {

              const step1Data = {
                report_name: data.report_name,
                request_type:
                  data.period === "Monthly"
                    ? 1
                    : data.period === "Quarterly"
                      ? 2
                      : data.period === "Annual"
                        ? 3
                        : 4,

                period_end_date,
                period_start_date,

                due_date: data.dueDate
                  ? dayjs(data.dueDate)
                    .startOf("day")
                    .toISOString()
                  : null,

                company: [],
                request_data: [],
                report_sent_on: new Date().toISOString(),
                status: 4,
                created_at: new Date().toISOString(),
              };

              await createReportItem(step1Data);

              setTimeout(() => {
                navigate("/activity-reports");
              }, 3000);

            } catch (error) {
              console.error("Error saving draft: ", error);
            }
          }}
          isValid={isValid}
          showPreview={false}
          nextLabel={isEditMode ? "Update & Next" : undefined}  // ADD THIS
        />
      </Box>
    </LocalizationProvider>
  );
};

export default Step1Settings;